/**
 * Cart Quick View (Popout Cart Test V1)
 *
 * Compact cart drawer add-ons: tapping an add-on's image/title (or ADD TO CART on a product
 * with options) slides a product panel up inside the cart drawer instead of opening the
 * product page. The panel shows a gallery, description and an option picker that swaps the
 * image, and adds the chosen variant straight to the cart using the built-in cart handlers.
 *
 * Runs at document level so it survives cart-drawer re-renders. Only loaded when the
 * "Add-ons: in-drawer quick view" theme setting is on, so it's fully revertible.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-quick-view';
  var loadToken = 0; // guards against overlapping fetches
  // Set when the panel is opened from a line item's (Edit) link:
  // { lineKey, lineQty, currentVariantId }. null for add-on quick view.
  var currentEdit = null;

  var SPINNER =
    '<div class="cart-qv-panel__loader"><svg width="22" height="22" viewBox="0 0 12 12" fill="none">' +
    '<circle cx="6" cy="6" r="5" stroke="#ddd" stroke-width="1.5" fill="none"/>' +
    '<path d="M6 1A5 5 0 0 1 11 6" stroke="#000" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg></div>';

  function getPanel() {
    return document.querySelector('[data-cart-quickview]');
  }

  /* ───────────────────────── Panel open / close ───────────────────────── */

  function openPanel() {
    var panel = getPanel();
    if (!panel) return;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    // Force reflow so the transform transition runs from the hidden state.
    // eslint-disable-next-line no-unused-expressions
    panel.offsetHeight;
    panel.classList.add('is-open');
    document.addEventListener('keydown', onKeydown);
  }

  function closePanel() {
    var panel = getPanel();
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    loadToken++; // cancel any in-flight load

    var sheet = panel.querySelector('.cart-qv-panel__sheet');
    var done = function () {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      var content = panel.querySelector('[data-qv-content]');
      if (content) content.innerHTML = '';
      if (sheet) sheet.removeEventListener('transitionend', onEnd);
      currentEdit = null;
    };
    var onEnd = function (e) {
      if (e.target === sheet && e.propertyName === 'transform') done();
    };
    if (sheet) {
      sheet.addEventListener('transitionend', onEnd);
      // Fallback in case the transitionend doesn't fire (e.g. reduced motion).
      setTimeout(done, 420);
    } else {
      done();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closePanel();
    }
  }

  /* ───────────────────────── Load a product ───────────────────────── */

  function loadProduct(productUrl, editCtx) {
    var panel = getPanel();
    if (!panel) return;
    var content = panel.querySelector('[data-qv-content]');
    if (!content) return;

    var token = ++loadToken;
    currentEdit = editCtx || null;
    content.innerHTML = SPINNER;
    openPanel();

    var path;
    try {
      path = new URL(productUrl, window.location.origin).pathname;
    } catch (err) {
      path = productUrl;
    }
    var fetchUrl = path + '?section_id=' + SECTION_ID;

    fetch(fetchUrl, { headers: { Accept: 'text/html' }, cache: 'no-store' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        if (token !== loadToken) return; // superseded or closed
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var root = doc.querySelector('[data-qv-root]');
        if (!root) {
          // Section render didn't return our markup — fall back to the product page.
          window.location.href = productUrl;
          return;
        }
        var body = panel.querySelector('[data-qv-content]');
        body.innerHTML = '';
        body.appendChild(document.importNode(root, true));
        body.scrollTop = 0;
        initContent(body.querySelector('[data-qv-root]'));
      })
      .catch(function (err) {
        console.error('Quick view load error:', err);
        if (token === loadToken) window.location.href = productUrl;
      });
  }

  /* ───────────────────────── Variant logic ───────────────────────── */

  function initContent(root) {
    if (!root) return;

    var variants = [];
    var dataEl = root.querySelector('[data-qv-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || [];
      } catch (err) {
        variants = [];
      }
    }

    // Thumbnails: swap the main image manually (does not change the selected variant).
    root.querySelectorAll('[data-qv-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-media-src');
        if (src) setMainImage(root, src);
        root.querySelectorAll('[data-qv-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    });

    // Option pills
    root.querySelectorAll('[data-qv-value]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-selected')) return;
        var position = btn.getAttribute('data-option-position');
        // Deselect siblings in the same option group, select this one.
        root
          .querySelectorAll('[data-qv-value][data-option-position="' + position + '"]')
          .forEach(function (sib) {
            sib.classList.toggle('is-selected', sib === btn);
          });
        var group = btn.closest('[data-qv-option]');
        if (group) {
          var label = group.querySelector('[data-qv-option-selected]');
          if (label) label.textContent = btn.getAttribute('data-option-value');
        }
        updateVariant(root, variants);
      });
    });

    // Add to cart / update line
    var addBtn = root.querySelector('[data-qv-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var variantId = addBtn.getAttribute('data-variant-id');
        if (!variantId || addBtn.disabled) return;
        if (currentEdit) {
          updateLineItem(addBtn, variantId, currentEdit);
        } else {
          addVariantToCart(addBtn, variantId);
        }
      });
    }

    // Compute per-value availability for the initial state.
    updateOptionAvailability(root, variants);

    // Edit mode: preselect the line item's current variant (relabels the button to Update).
    if (currentEdit) {
      preselectVariant(root, variants, currentEdit.currentVariantId);
    }
  }

  // Select the pills matching a given variant's option values, then refresh state.
  function preselectVariant(root, variants, variantId) {
    if (!variantId) {
      updateVariant(root, variants);
      return;
    }
    var variant = (variants || []).find(function (v) {
      return String(v.id) === String(variantId);
    });
    if (variant && variant.options) {
      variant.options.forEach(function (val, i) {
        var pos = i + 1;
        root
          .querySelectorAll('[data-qv-value][data-option-position="' + pos + '"]')
          .forEach(function (btn) {
            var isMatch = btn.getAttribute('data-option-value') === val;
            btn.classList.toggle('is-selected', isMatch);
            if (isMatch) {
              var group = btn.closest('[data-qv-option]');
              var lbl = group && group.querySelector('[data-qv-option-selected]');
              if (lbl) lbl.textContent = val;
            }
          });
      });
    }
    updateVariant(root, variants);
  }

  function getSelectedValues(root) {
    var values = [];
    root.querySelectorAll('[data-qv-option]').forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      var selected = group.querySelector('[data-qv-value].is-selected');
      values[pos - 1] = selected ? selected.getAttribute('data-option-value') : null;
    });
    return values;
  }

  function findVariant(variants, selectedValues) {
    return variants.find(function (v) {
      return (v.options || []).every(function (opt, i) {
        return opt === selectedValues[i];
      });
    });
  }

  function setMainImage(root, src) {
    var img = root.querySelector('[data-qv-main-image]');
    if (!img || !src) return;
    img.src = src.indexOf('width=') === -1 ? src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=700' : src;
  }

  function updateVariant(root, variants) {
    var selected = getSelectedValues(root);
    var variant = findVariant(variants, selected);
    var addBtn = root.querySelector('[data-qv-add]');
    var label = root.querySelector('[data-qv-add-label]');
    var priceEl = root.querySelector('[data-qv-price]');
    var compareEl = root.querySelector('[data-qv-compare]');
    var saveEl = root.querySelector('[data-qv-save]');

    updateOptionAvailability(root, variants);

    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.removeAttribute('data-variant-id');
      }
      if (label) label.textContent = 'Unavailable';
      return;
    }

    if (variant.image) setMainImage(root, variant.image);
    if (priceEl && variant.price) priceEl.textContent = variant.price;
    if (compareEl) {
      if (variant.compare_gt && variant.compare_at) {
        compareEl.textContent = variant.compare_at;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }
    if (saveEl) {
      if (variant.save_gt && variant.save) {
        saveEl.textContent = 'Save ' + variant.save;
        saveEl.hidden = false;
      } else {
        saveEl.textContent = '';
        saveEl.hidden = true;
      }
    }
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', variant.id);
      addBtn.disabled = !variant.available;
    }
    if (label) {
      label.textContent = variant.available ? (currentEdit ? 'Update' : 'Add to cart') : 'Sold out';
    }
  }

  // Gray out option values whose combination with the other selected options has no
  // available variant, so the picker guides toward valid choices.
  function updateOptionAvailability(root, variants) {
    var selected = getSelectedValues(root);
    root.querySelectorAll('[data-qv-value]').forEach(function (btn) {
      var pos = parseInt(btn.getAttribute('data-option-position'), 10);
      var candidate = selected.slice();
      candidate[pos - 1] = btn.getAttribute('data-option-value');
      var match = variants.find(function (v) {
        return (v.options || []).every(function (opt, i) {
          // Ignore positions we haven't decided yet (null) except this one.
          if (i === pos - 1) return opt === candidate[i];
          return candidate[i] == null || opt === candidate[i];
        });
      });
      var available = match ? match.available : false;
      btn.classList.toggle('is-unavailable', !available);
    });
  }

  /* ───────────────────────── Add to cart ───────────────────────── */

  function addVariantToCart(btn, variantId) {
    btn.disabled = true;
    var label = btn.querySelector('[data-qv-add-label]');
    var originalText = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response.status) {
          console.error('Quick view add error:', response.description);
          btn.disabled = false;
          if (label) label.textContent = originalText || 'Add to cart';
          return;
        }
        // Reuse cart-upsell.js's refresh path so the drawer + bubble update identically.
        var api = window.VPCartUpsell || {};
        if (typeof api.updateCartBubble === 'function') api.updateCartBubble();
        if (typeof api.refreshCartDrawerItems === 'function') api.refreshCartDrawerItems();
        else {
          document.dispatchEvent(new Event('cart:updated'));
          document.dispatchEvent(new Event('theme:cart:updated'));
        }
        if (label) label.textContent = 'Added ✓';
        setTimeout(closePanel, 450);
      })
      .catch(function (err) {
        console.error('Quick view add error:', err);
        btn.disabled = false;
        if (label) label.textContent = originalText || 'Add to cart';
      });
  }

  // Edit mode: swap a line item to a different variant. Shopify has no in-place variant change,
  // so add the chosen variant (keeping the line's quantity) then remove the original line. The
  // replaced item lands at the end of the cart.
  function updateLineItem(btn, newVariantId, editCtx) {
    // Nothing changed — just close.
    if (String(newVariantId) === String(editCtx.currentVariantId)) {
      closePanel();
      return;
    }
    btn.disabled = true;
    var label = btn.querySelector('[data-qv-add-label]');
    var originalText = label ? label.textContent : '';
    if (label) label.textContent = 'Updating…';
    var qty = editCtx.lineQty > 0 ? editCtx.lineQty : 1;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(newVariantId, 10), quantity: qty }] }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || 'add failed');
        }
        // Only remove the original line once the new variant is safely in the cart.
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: editCtx.lineKey, quantity: 0 }),
        });
      })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        var api = window.VPCartUpsell || {};
        if (typeof api.updateCartBubble === 'function') api.updateCartBubble();
        if (typeof api.refreshCartDrawerItems === 'function') api.refreshCartDrawerItems();
        else {
          document.dispatchEvent(new Event('cart:updated'));
          document.dispatchEvent(new Event('theme:cart:updated'));
        }
        if (label) label.textContent = 'Updated ✓';
        setTimeout(closePanel, 450);
      })
      .catch(function (err) {
        console.error('Quick view update error:', err);
        btn.disabled = false;
        if (label) label.textContent = originalText || 'Update';
      });
  }

  /* ───────────────────────── Event delegation ───────────────────────── */

  // Triggers live inside the recommendations block which re-renders, so delegate on document.
  document.addEventListener(
    'click',
    function (e) {
      var closeEl = e.target.closest('[data-qv-close]');
      if (closeEl && getPanel() && getPanel().contains(closeEl)) {
        e.preventDefault();
        closePanel();
        return;
      }

      var trigger = e.target.closest('[data-qv-trigger]');
      if (!trigger) return;
      var isEdit = trigger.getAttribute('data-qv-mode') === 'edit';
      // Add-on triggers live in #CartDrawerUpsell; edit triggers live on the cart line items.
      if (!isEdit && !e.target.closest('#CartDrawerUpsell')) return;
      e.preventDefault();
      e.stopPropagation();
      var url = trigger.getAttribute('data-product-url') || trigger.getAttribute('href');
      var editCtx = null;
      if (isEdit) {
        editCtx = {
          lineKey: trigger.getAttribute('data-line-key'),
          lineQty: parseInt(trigger.getAttribute('data-line-qty'), 10) || 1,
          currentVariantId: trigger.getAttribute('data-variant-id'),
        };
      }
      if (url) loadProduct(url, editCtx);
    },
    true // capture: beat the recommendation card's own stretched-link handlers
  );
})();
